<template>
  <div>
    <TitlePage title="Notifiche push" />

    <!-- UCAMP-87 plan 6: le campagne push si gestiscono dal marketing; l'invio resta sempre qui sull'engine -->
    <v-alert type="info" outlined dense class="mb-4">
      Le campagne push si gestiscono ora da
      <a :href="marketingPushUrl" target="_blank" rel="noopener">marketing.ucamp.it</a>:
      segmenti per centro, città e posizione, programmazione, template e storico completo.
      Gli invii del marketing compaiono anche qui, in "Notifiche inviate".
    </v-alert>

    <v-row class="mb-4">
      <v-col v-for="card in statCards" :key="card.label" cols="12" md="3">
        <v-card outlined class="push-stat-card">
          <v-icon color="secondary" class="mb-2">{{ card.icon }}</v-icon>
          <div class="push-stat-card__value">{{ card.value }}</div>
          <div class="push-stat-card__label">{{ card.label }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab>
          <v-icon left>mdi-send</v-icon>
          Invio
        </v-tab>
        <v-tab>
          <v-icon left>mdi-account-bell</v-icon>
          Iscritti
        </v-tab>
        <v-tab>
          <v-icon left>mdi-history</v-icon>
          Notifiche inviate
        </v-tab>
        <v-tab>
          <v-icon left>mdi-bell-outline</v-icon>
          Notifiche Automatiche
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-row class="pa-4">
            <v-col cols="12" md="7">
              <v-card outlined>
                <v-card-title>Nuova notifica</v-card-title>
                <v-card-text>
                  <v-text-field v-model="form.title" label="Titolo" outlined dense />
                  <v-textarea v-model="form.body" label="Messaggio" outlined rows="4" />
                  <v-text-field
                    v-model="form.url"
                    label="URL al click"
                    hint="Esempio: /profile oppure https://www.ucamp.it/..."
                    persistent-hint
                    outlined
                    dense
                  />

                  <v-select
                    v-model="form.segment"
                    :items="segmentOptions"
                    item-text="text"
                    item-value="value"
                    label="Pubblico"
                    outlined
                    dense
                  />

                  <v-select
                    v-model="form.platforms"
                    :items="platformOptions"
                    item-text="text"
                    item-value="value"
                    label="Canali"
                    multiple
                    chips
                    outlined
                    dense
                  />

                  <v-switch
                    v-model="form.create_inbox"
                    label="Aggiungi anche alla lista notifiche degli utenti loggati"
                    inset
                    dense
                    hide-details
                  />
                  <v-switch
                    v-model="form.dry_run"
                    label="Test tecnico senza invio reale"
                    inset
                    dense
                    hide-details
                    class="mt-2"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-btn color="primary" :loading="previewLoading" @click="previewTarget">
                    <v-icon left>mdi-eye</v-icon>
                    Anteprima pubblico
                  </v-btn>
                  <v-spacer />
                  <v-btn color="secondary" :loading="sendLoading" :disabled="!canSend" @click="sendNotification">
                    <v-icon left>mdi-send</v-icon>
                    Invia notifica
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <v-col cols="12" md="5">
              <v-card outlined class="push-preview-card">
                <v-card-title>Anteprima</v-card-title>
                <v-card-text>
                  <div class="phone-preview">
                    <div class="phone-preview__title">{{ form.title || 'Titolo notifica' }}</div>
                    <div class="phone-preview__body">{{ form.body || 'Il messaggio apparirà qui.' }}</div>
                    <div class="phone-preview__url">{{ form.url || 'Nessun URL impostato' }}</div>
                  </div>

                  <v-divider class="my-4" />

                  <div class="target-preview">
                    <div class="target-preview__item">
                      <strong>{{ targetPreview.devices }}</strong>
                      <span>device target</span>
                    </div>
                    <div class="target-preview__item">
                      <strong>{{ targetPreview.users }}</strong>
                      <span>utenti con inbox</span>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>

        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="3">
                <v-select v-model="subscriptionFilters.platform" :items="platformFilterOptions" label="Canale" outlined dense @change="loadSubscriptions(true)" />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="subscriptionFilters.segment" :items="segmentOptions" item-text="text" item-value="value" label="Pubblico" outlined dense @change="loadSubscriptions(true)" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="subscriptionFilters.search" label="Cerca utente, device o piattaforma" append-icon="mdi-magnify" outlined dense clearable @input="debouncedSubscriptions" />
              </v-col>
            </v-row>

            <v-data-table
              :headers="subscriptionHeaders"
              :items="subscriptions"
              :loading="subscriptionsLoading"
              disable-pagination
              hide-default-footer
            >
              <template v-slot:item.platform="{ item }">
                <v-chip small color="primary" text-color="white">{{ item.platform }}</v-chip>
              </template>
              <template v-slot:item.customer="{ item }">
                <span v-if="item.customer">{{ item.customer.name }} {{ item.customer.surname }} - {{ item.customer.email }}</span>
                <v-chip v-else small>Ospite</v-chip>
              </template>
              <template v-slot:item.updated_at="{ item }">{{ formatDate(item.updated_at) }}</template>
            </v-data-table>

            <div class="text-center pt-3">
              <v-pagination v-model="subscriptionsPage" :length="subscriptionPages" @input="loadSubscriptions()" />
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="campaignSource"
                  :items="campaignSourceOptions"
                  item-text="text"
                  item-value="value"
                  label="Origine"
                  outlined
                  dense
                  @change="campaignsPage = 1; loadCampaigns()"
                />
              </v-col>
            </v-row>
            <v-data-table
              :headers="campaignHeaders"
              :items="campaigns"
              :loading="campaignsLoading"
              disable-pagination
              hide-default-footer
            >
              <template v-slot:item.platforms="{ item }">
                <v-chip v-for="platform in item.platforms" :key="platform" small class="mr-1">{{ platform }}</v-chip>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip v-if="item.dry_run" small color="grey" text-color="white">test</v-chip>
                <v-chip v-else small :color="campaignStatusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
              </template>
              <template v-slot:item.source="{ item }">
                <v-chip small outlined :color="sourceColor(item.source)">{{ sourceLabel(item.source) }}</v-chip>
              </template>
              <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
            </v-data-table>

            <div class="text-center pt-3">
              <v-pagination v-model="campaignsPage" :length="campaignPages" @input="loadCampaigns()" />
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="autoNotifFilters.customer_id"
                  label="ID Customer"
                  outlined
                  dense
                  clearable
                  @input="debouncedAutoNotif"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="autoNotifFilters.type"
                  :items="autoNotifTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="Tipo"
                  outlined
                  dense
                  clearable
                  @change="loadAutoNotifications(true)"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="autoNotifFilters.read"
                  :items="autoNotifReadOptions"
                  item-text="text"
                  item-value="value"
                  label="Stato lettura"
                  outlined
                  dense
                  clearable
                  @change="loadAutoNotifications(true)"
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="autoNotifHeaders"
              :items="autoNotifications"
              :loading="autoNotifLoading"
              disable-pagination
              hide-default-footer
            >
              <template v-slot:item.customer_id="{ item }">
                <span v-if="item.customer_id">
                  {{ item.customer_id.name }} {{ item.customer_id.surname }}<br/>
                  <small>{{ item.customer_id.email }}</small>
                </span>
                <span v-else>-</span>
              </template>
              <template v-slot:item.type="{ item }">
                <v-chip small>{{ item.type }}</v-chip>
              </template>
              <template v-slot:item.read="{ item }">
                <v-icon :color="item.read ? 'success' : 'grey'">
                  {{ item.read ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                </v-icon>
              </template>
              <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
            </v-data-table>

            <div class="text-center pt-3">
              <v-pagination v-model="autoNotifPage" :length="autoNotifPages" @input="loadAutoNotifications()" />
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'

import TitlePage from '@/components/utils/TitlePage'
import { marketingPushUrl } from '@/utils/platforms'

export default {
  name: 'PushNotificationsAdmin',
  components: { TitlePage },
  data() {
    return {
      tab: 0,
      stats: {},
      form: {
        title: '',
        body: '',
        url: '',
        segment: 'all',
        platforms: ['web', 'android', 'ios'],
        create_inbox: true,
        dry_run: false,
      },
      targetPreview: {
        devices: 0,
        users: 0,
      },
      previewLoading: false,
      sendLoading: false,
      subscriptionsLoading: false,
      campaignsLoading: false,
      subscriptions: [],
      subscriptionsCount: 0,
      subscriptionsPage: 1,
      subscriptionsLimit: 25,
      campaigns: [],
      campaignsCount: 0,
      campaignsPage: 1,
      campaignsLimit: 25,
      subscriptionFilters: {
        platform: '',
        segment: 'all',
        search: '',
      },
      segmentOptions: [
        { text: 'Tutti gli iscritti', value: 'all' },
        { text: 'Utenti loggati', value: 'logged' },
        { text: 'Ospiti', value: 'guests' },
      ],
      platformOptions: [
        { text: 'Web', value: 'web' },
        { text: 'Android', value: 'android' },
        { text: 'iOS', value: 'ios' },
      ],
      platformFilterOptions: [
        { text: 'Tutti', value: '' },
        { text: 'Web', value: 'web' },
        { text: 'Android', value: 'android' },
        { text: 'iOS', value: 'ios' },
      ],
      subscriptionHeaders: [
        { text: 'Canale', value: 'platform' },
        { text: 'Utente', value: 'customer' },
        { text: 'Device', value: 'device_id' },
        { text: 'Lingua', value: 'locale' },
        { text: 'Ultimo aggiornamento', value: 'updated_at' },
      ],
      campaignSource: '',
      campaignSourceOptions: [
        { text: 'Tutte', value: '' },
        { text: 'Admin', value: 'admin' },
        { text: 'Marketing', value: 'marketing' },
        { text: 'Automatica marketing (es. promo SA)', value: 'marketing_auto' },
      ],
      campaignHeaders: [
        { text: 'Titolo', value: 'title' },
        { text: 'Origine', value: 'source' },
        { text: 'Pubblico', value: 'segment' },
        { text: 'Canali', value: 'platforms' },
        { text: 'Target', value: 'target_count' },
        { text: 'Inviate', value: 'sent_count' },
        { text: 'Errori', value: 'failure_count' },
        { text: 'Status', value: 'status' },
        { text: 'Data', value: 'created_at' },
      ],
      autoNotifLoading: false,
      autoNotifications: [],
      autoNotifCount: 0,
      autoNotifPage: 1,
      autoNotifLimit: 25,
      autoNotifFilters: {
        customer_id: '',
        type: '',
        read: '',
      },
      autoNotifTypeOptions: [
        { text: 'Tutti', value: '' },
        { text: 'Prenotazione', value: 'booking' },
        { text: 'Promemoria', value: 'reminder' },
        { text: 'Cancellazione', value: 'cancellation' },
        { text: 'Partita creata', value: 'match_created' },
        { text: 'Iscrizione partita', value: 'match_joined' },
        { text: 'Uscita partita', value: 'match_left' },
        { text: 'Partita al completo', value: 'match_full' },
        { text: 'Partita annullata', value: 'match_cancelled' },
        { text: 'Campo prenotato', value: 'match_booking_linked' },
        { text: 'Cambio partecipanti', value: 'match_participant_change' },
        { text: 'Statistiche partita', value: 'match_stats' },
      ],
      autoNotifReadOptions: [
        { text: 'Tutte', value: '' },
        { text: 'Non lette', value: 'false' },
        { text: 'Lette', value: 'true' },
      ],
      autoNotifHeaders: [
        { text: 'Customer', value: 'customer_id' },
        { text: 'Titolo', value: 'title' },
        { text: 'Tipo', value: 'type' },
        { text: 'Letta', value: 'read' },
        { text: 'Data', value: 'created_at' },
      ],
    }
  },
  computed: {
    marketingPushUrl() {
      return marketingPushUrl()
    },
    canSend() {
      return this.form.title.trim() && this.form.body.trim() && this.form.platforms.length
    },
    statCards() {
      const byPlatform = this.stats.byPlatform || []
      const bySegment = this.stats.bySegment || []
      return [
        { label: 'Web', icon: 'mdi-web', value: this.findCount(byPlatform, 'web') },
        { label: 'Android', icon: 'mdi-android', value: this.findCount(byPlatform, 'android') },
        { label: 'iOS', icon: 'mdi-apple', value: this.findCount(byPlatform, 'ios') },
        { label: 'Utenti loggati', icon: 'mdi-account-check', value: this.findCount(bySegment, 'logged') },
      ]
    },
    subscriptionPages() {
      return Math.max(1, Math.ceil(this.subscriptionsCount / this.subscriptionsLimit))
    },
    campaignPages() {
      return Math.max(1, Math.ceil(this.campaignsCount / this.campaignsLimit))
    },
    autoNotifPages() {
      return Math.max(1, Math.ceil(this.autoNotifCount / this.autoNotifLimit))
    },
  },
  async created() {
    this.debouncedSubscriptions = this.debounce(() => this.loadSubscriptions(true), 350)
    this.debouncedAutoNotif = this.debounce(() => this.loadAutoNotifications(true), 350)
    await Promise.all([
      this.loadStats(),
      this.previewTarget(),
      this.loadSubscriptions(),
      this.loadCampaigns(),
      this.loadAutoNotifications(),
    ])
  },
  methods: {
    ...mapMutations({
      setLoading: 'settings/setLoading',
      setNotification: 'settings/setNotification',
    }),
    ...mapActions({
      getStats: 'pushAdmin/stats',
      getSubscriptions: 'pushAdmin/subscriptions',
      getCampaigns: 'pushAdmin/campaigns',
      getPreview: 'pushAdmin/preview',
      sendPush: 'pushAdmin/send',
      getCustomerNotifications: 'pushAdmin/customerNotifications',
    }),
    debounce(fn, wait) {
      let timer = null
      return (...args) => {
        clearTimeout(timer)
        timer = setTimeout(() => fn(...args), wait)
      }
    },
    findCount(collection, key) {
      const item = collection.find(row => row._id === key)
      return item ? item.count : 0
    },
    async loadStats() {
      const result = await this.getStats()
      if (result && result.success !== false) {
        this.stats = result
      }
    },
    async previewTarget() {
      this.previewLoading = true
      const result = await this.getPreview(this.form)
      if (result && result.devices !== undefined) {
        this.targetPreview = result
      }
      this.previewLoading = false
    },
    async sendNotification() {
      if (!this.canSend) {
        this.setNotification({ active: true, color: 'error', mode: 'multi-line', text: 'Titolo, messaggio e canali sono obbligatori' })
        return
      }

      this.sendLoading = true
      const result = await this.sendPush(this.form)
      this.sendLoading = false

      if (result.success) {
        this.setNotification({ active: true, color: 'success', mode: 'multi-line', text: 'Notifica elaborata. Controlla lo storico invii.' })
        await Promise.all([this.loadStats(), this.previewTarget(), this.loadSubscriptions(), this.loadCampaigns()])
        return
      }

      this.setNotification({ active: true, color: 'error', mode: 'multi-line', text: result })
    },
    async loadSubscriptions(reset = false) {
      if (reset) this.subscriptionsPage = 1
      this.subscriptionsLoading = true
      const result = await this.getSubscriptions({
        page: this.subscriptionsPage,
        limit: this.subscriptionsLimit,
        ...this.subscriptionFilters,
      })
      if (result && Array.isArray(result.results)) {
        this.subscriptions = result.results
        this.subscriptionsCount = result.count
      }
      this.subscriptionsLoading = false
    },
    sourceLabel(source) {
      return ({ marketing: 'Marketing', marketing_auto: 'Automatica marketing' })[source] || 'Admin'
    },
    sourceColor(source) {
      return ({ marketing: 'primary', marketing_auto: 'purple' })[source] || 'grey darken-1'
    },
    async loadCampaigns() {
      this.campaignsLoading = true
      const result = await this.getCampaigns({
        page: this.campaignsPage,
        limit: this.campaignsLimit,
        source: this.campaignSource,
      })
      if (result && Array.isArray(result.results)) {
        this.campaigns = result.results
        this.campaignsCount = result.count
      }
      this.campaignsLoading = false
    },
    async loadAutoNotifications(reset = false) {
      if (reset) this.autoNotifPage = 1
      this.autoNotifLoading = true
      const result = await this.getCustomerNotifications({
        page: this.autoNotifPage,
        limit: this.autoNotifLimit,
        ...this.autoNotifFilters,
      })
      if (result && Array.isArray(result.data)) {
        this.autoNotifications = result.data
        this.autoNotifCount = result.total
      }
      this.autoNotifLoading = false
    },
    campaignStatusColor(status) {
      if (status === 'sent') return 'success'
      if (status === 'failed') return 'error'
      return 'warning'
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString()
    },
  },
}
</script>

<style scoped>
.push-stat-card {
  border-radius: 6px;
  padding: 16px;
}

.push-stat-card__value {
  color: #1f2937;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
}

.push-stat-card__label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  margin-top: 6px;
}

.phone-preview {
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  padding: 14px;
  background: #f8fafc;
}

.phone-preview__title {
  color: #0f172a;
  font-size: 14px;
  font-weight: 900;
}

.phone-preview__body {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}

.phone-preview__url {
  color: #25a639;
  font-size: 12px;
  font-weight: 800;
  margin-top: 10px;
  word-break: break-word;
}

.target-preview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.target-preview__item {
  border-radius: 6px;
  padding: 12px;
  background: #f1f5f9;
}

.target-preview__item strong {
  display: block;
  color: #0f172a;
  font-size: 22px;
  line-height: 1;
}

.target-preview__item span {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  margin-top: 6px;
}
</style>
